<template>
  <v-card class="qr-template-dialog">
    <v-card-title class="justify-center text-center text-h5 font-weight-bold primary--text px-8 pt-6 pb-2" style="word-break: keep-all">
      ตั้งค่า QR Code รับเงินจากแอป ร้านค้า ต่างๆ
    </v-card-title>
    <v-card-text class="px-4 px-md-8 pt-2 pb-4">
      <v-stepper :value="stepNumber" flat alt-labels class="elevation-0 mb-2">
        <v-stepper-header class="elevation-0">
          <v-stepper-step :complete="stepNumber > 1" step="1">อัปโหลด QR</v-stepper-step>
          <v-divider />
          <v-stepper-step :complete="stepNumber > 2" step="2">ตรวจข้อมูลและทดสอบโอน 1 บาท</v-stepper-step>
        </v-stepper-header>
      </v-stepper>

      <v-alert v-if="error" type="error" dense text class="qr-alert mb-4" data-test="qr-template-error">
        {{ error }}
      </v-alert>

      <!-- 1. upload -->
      <div v-if="step === 'upload' || step === 'sample'">
        <template v-if="step === 'upload'">
          <p class="qr-text mb-3">
            อัปโหลดรูป <b>QR รับเงินของร้าน</b> จากแอพร้านค้า แบบ <b>ไม่ระบุยอดเงิน</b>
            (เช่น หน้า "QR รับเงิน" ของ K Shop / แม่มณี / เป๋าตัง) แล้วแคปหน้าจอมาอัปโหลด
          </p>
        </template>
        <template v-else>
          <v-alert type="info" dense text class="qr-alert mb-3">
            ทดสอบไม่ผ่านก็ไม่เป็นไรครับ กรุณาอัปโหลด QR อีกรูปจาก <b>แอปและบัญชีเดียวกัน</b>
            โดยกด <b>"ระบุยอดเงิน"</b> ในแอป (จะใส่ยอดเท่าไรก็ได้) แล้วแคปหน้าจอมา
            ระบบจะเรียนรู้วิธีที่แอปนั้นสร้าง QR แล้วให้ทดสอบใหม่
          </v-alert>
        </template>
        <p class="qr-hint mb-4">
          ระบบอ่านรหัสจากรูปในเครื่องของคุณ ไม่ได้อัปโหลดหรือเก็บรูป เก็บเฉพาะข้อมูลรับเงินที่อ่านได้
        </p>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          class="d-none"
          data-test="qr-template-file"
          @change="onFileChosen"
        />
        <div
          :class="['qr-upload-box', { 'qr-upload-box--over': dragging }]"
          data-test="qr-template-dropzone"
          @dragenter.prevent="dragging = !busy"
          @dragover.prevent="dragging = !busy"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onDrop"
        >
          <div><v-icon size="56" color="primary">mdi-qrcode-scan</v-icon></div>
          <div class="qr-upload-box__text mt-2">
            {{ dragging ? 'ปล่อยเมาส์เพื่ออัปโหลดรูป' : 'ลากรูป QR มาวางที่นี่ หรือกดปุ่มด้านล่าง' }}
          </div>
          <v-btn color="primary" class="mt-3" :loading="busy" :disabled="busy" @click="$refs.fileInput.click()">
            <v-icon left>mdi-upload</v-icon>
            {{ step === 'sample' ? 'เลือกรูป QR ที่ระบุยอดเงินแล้ว' : 'เลือกรูป QR' }}
          </v-btn>
        </div>
        <div class="mt-4">
          <v-btn text small color="primary" @click="showPaste = !showPaste">
            อ่านรูปไม่ได้? วางข้อความจาก QR แทน
          </v-btn>
          <div v-if="showPaste" class="mt-2">
            <v-textarea
              v-model="pasteText"
              outlined
              dense
              rows="3"
              hide-details
              label="ข้อความจาก QR (ขึ้นต้นด้วย 000201...)"
              data-test="qr-template-paste"
            />
            <v-btn class="mt-2" small color="primary" :disabled="busy || !pasteText.trim()" @click="submitText">
              ใช้ข้อความนี้
            </v-btn>
          </div>
        </div>
      </div>

      <!-- 2. read data + 1 baht test, on one screen -->
      <div v-else-if="step === 'test'">
        <div class="text-center">
          <p class="qr-text mb-3">
            สแกน QR นี้ด้วยแอพ<b>ธนาคารอื่น</b> แล้ว <b class="qr-amount">โอนเงิน {{ testAmount }} บาท</b> เข้าบัญชีร้านของคุณจริง
          </p>
          <div v-if="testQrImage" class="qr-image-card">
            <img :src="testQrImage" width="260" height="260" alt="QR ทดสอบ 1 บาท" data-test="qr-template-test-qr" />
          </div>
          <p class="qr-hint mt-3">
            ตรวจว่ายอด {{ testAmount }} ขึ้นในแอปอัตโนมัติ และชื่อผู้รับตรงกับร้านของคุณ
            แล้วกดผลการทดสอบด้านล่าง
          </p>
        </div>

        <div class="mt-4">
          <v-text-field
            v-model="label"
            label="ข้อความแสดงใต้ QR (ไม่บังคับ)"
            hint="แสดงที่จอลูกค้าและในใบพิมพ์ เช่น ชื่อร้านหรือชื่อบัญชีที่รับเงิน แก้ไขได้ภายหลังในหน้าตั้งค่าสาขา"
            persistent-hint
            counter="80"
            maxlength="80"
            outlined
            dense
            data-test="qr-template-label-input"
          />
        </div>

        <v-expansion-panels flat class="qr-details mt-4" data-test="qr-template-details">
          <v-expansion-panel>
            <v-expansion-panel-header class="qr-details__header" data-test="qr-template-details-toggle">
              <span>
                ตรวจสอบข้อมูลที่อ่านจาก QR
                <v-chip v-if="warnings.length" small color="warning" class="ml-2">มีคำเตือน</v-chip>
              </span>
            </v-expansion-panel-header>
            <v-expansion-panel-content eager>
            <p class="qr-text mb-3">
              กรุณา <b>เทียบกับที่เห็นในแอปธนาคารของร้าน</b>
              (เช่น รหัสร้านค้า / เลขอ้างอิง / G-Wallet ID)
              <v-btn text color="primary" class="ml-1" :disabled="busy" data-test="qr-template-restart" @click="restart">
                ไม่ตรง? อัปโหลดใหม่
              </v-btn>
            </p>
            <v-alert v-if="template && template.payee_name" type="info" dense text class="qr-alert mb-2" data-test="qr-template-payee-name">
              ชื่อร้านที่ QR ระบุไว้: <b>{{ template.payee_name }}</b><br />
              ระบบจะใช้เป็นข้อความใต้ QR ให้ (ถ้าคุณยังไม่ได้กรอกไว้) แก้ไขได้ภายหลังในหน้าตั้งค่าสาขา
            </v-alert>
            <v-alert v-else type="info" dense text class="qr-alert mb-2" data-test="qr-template-no-name">
              QR นี้ไม่มีชื่อร้านอยู่ในตัว (ชื่อที่เห็นในรูปแอปธนาคารเป็นข้อความที่แอปวาดเพิ่ม)
              กรุณากรอกชื่อร้านหรือชื่อบัญชีในช่องด้านบน เพื่อแสดงใต้ QR ให้ลูกค้ารู้ว่าโอนให้ใคร
            </v-alert>
            <v-alert v-for="warning in warnings" :key="warning.code" type="warning" dense text class="qr-alert mb-2">
              {{ warning.message }}
            </v-alert>
            <v-simple-table class="qr-refs mb-4" data-test="qr-template-references">
              <template #default>
                <tbody>
                  <template v-for="(reference, index) in references">
                    <tr :key="'h' + index" class="grey lighten-4">
                      <td colspan="2" class="qr-ref-head"><b>รายการที่ {{ index + 1 }}</b> (รหัสผู้ให้บริการ {{ reference.aid || '-' }})</td>
                    </tr>
                    <tr v-for="field in reference.fields" :key="index + '-' + field.tag">
                      <td class="text-no-wrap grey--text">ช่อง {{ field.tag }}</td>
                      <td>{{ field.value }}</td>
                    </tr>
                  </template>
                  <tr v-if="!references.length">
                    <td colspan="2" class="grey--text">ไม่พบรายการบัญชีผู้รับเงินที่รู้จัก</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <!-- 4a. failed -->
      <div v-else-if="step === 'failed'">
        <v-alert type="warning" dense text class="qr-alert mb-2">
          ทดสอบไม่ผ่าน QR นี้ยังไม่ถูกนำไปใช้ และร้านยังใช้การตั้งค่าเดิมอยู่
        </v-alert>
        <p class="qr-text mb-3">เลือกอย่างใดอย่างหนึ่ง</p>
        <v-btn color="primary" class="mb-2" @click="goToSample">
          <v-icon left>mdi-qrcode-scan</v-icon> อัปโหลด QR ที่ระบุยอดเงินแล้ว (ให้ระบบเรียนรู้)
        </v-btn>
        <p class="qr-hint">
          ถ้ายังไม่ได้ผล กรุณาติดต่อทีมงาน LINE: @p9944 พร้อมแจ้งรหัสอ้างอิง <b data-test="qr-template-ref">{{ template && template.ref }}</b>
        </p>
      </div>

      <!-- 5. done -->
      <div v-else-if="step === 'done'">
        <v-alert type="success" dense text>
          ตั้งค่าเรียบร้อย ระบบจะใช้ QR นี้พร้อมยอดเงินของแต่ละบิล
        </v-alert>
      </div>
    </v-card-text>

    <v-card-actions class="px-8 pt-2 pb-6">
      <v-spacer />
      <template v-if="step === 'test'">
        <v-btn outlined color="error" class="px-5 mr-3" :disabled="busy" data-test="qr-template-test-failed" @click="report(false)">ใช้ไม่ได้</v-btn>
        <v-btn color="primary" class="px-8" :loading="busy" :disabled="busy" data-test="qr-template-test-passed" @click="report(true)">โอนสำเร็จ ใช้งานได้</v-btn>
      </template>
      <template v-else-if="step === 'done'">
        <v-btn color="primary" class="px-8" data-test="qr-template-done" @click="finish">ปิด</v-btn>
      </template>
      <template v-else>
        <v-btn outlined color="grey" class="px-5" :disabled="busy" @click="$emit('onClose')">ปิด</v-btn>
      </template>
    </v-card-actions>
  </v-card>
</template>

<script>
import QRCode from 'qrcode';
import { decodeQrFromFile } from './decodeQrImage';

export default {
  props: {
    branchId: { type: String, required: true },
    // the payee text the branch already has, offered again so it is not lost
    initialLabel: { type: String, default: '' },
  },
  data() {
    return {
      step: 'upload', // upload | test | failed | sample | done
      template: null,
      busy: false,
      error: '',
      showPaste: false,
      pasteText: '',
      testQrImage: '',
      label: this.initialLabel,
      dragging: false,
    };
  },
  computed: {
    stepNumber() {
      if (this.step === 'upload' || this.step === 'sample') return 1;
      if (this.step === 'done') return 3;
      return 2;
    },
    references() {
      return (this.template && this.template.inspection && this.template.inspection.references) || [];
    },
    warnings() {
      return (this.template && this.template.inspection && this.template.inspection.warnings) || [];
    },
    testAmount() {
      return (this.template && this.template.test_amount) || '1.00';
    },
  },
  methods: {
    async call(path, data) {
      const body = {
        token: this.$jwt.sign(
          { branch_id: this.branchId, ...data },
          this.$privateKey,
          { noTimestamp: true }
        ),
      };
      const response = await this.$axios.post(`${this.$baseUrl}/qr_template/${path}`, body);
      return response.result;
    },
    async run(work) {
      this.busy = true;
      this.error = '';
      try {
        await work();
      } catch (err) {
        this.error = (err && (err.error_message || err.message)) || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
      } finally {
        this.busy = false;
      }
    },
    async onFileChosen(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      await this.readFile(file);
    },
    // Leaving a child element also fires dragleave; only react when the pointer leaves the box.
    onDragLeave(event) {
      if (!event.currentTarget.contains(event.relatedTarget)) this.dragging = false;
    },
    async onDrop(event) {
      this.dragging = false;
      if (this.busy) return;
      await this.readFile(event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]);
    },
    // Shared by the file button and drag & drop. decodeQrFromFile refuses non-image files.
    async readFile(file) {
      if (!file) return;
      await this.run(async () => {
        const text = await decodeQrFromFile(file);
        if (!text) {
          throw new Error('อ่าน QR จากรูปนี้ไม่ได้ กรุณาใช้รูปที่ชัดและเห็น QR ครบทั้งกรอบ หรือวางข้อความจาก QR แทน');
        }
        await this.submitPayload(text);
      });
    },
    async submitText() {
      await this.run(() => this.submitPayload(this.pasteText.trim()));
    },
    // The server is the judge: it validates the text and rejects anything unusable.
    async submitPayload(payload) {
      if (this.step === 'sample') {
        this.template = await this.call('submit_sample', {
          template_id: this.template._id,
          sample_payload: payload,
        });
        await this.showTest();
        return;
      }
      this.template = await this.call('submit', { payload });
      await this.showTest();
    },
    async showTest() {
      this.testQrImage = await QRCode.toDataURL(this.template.test_payload, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 260,
      });
      this.step = 'test';
    },
    async report(passed) {
      await this.run(async () => {
        this.template = await this.call('report_test', {
          template_id: this.template._id,
          passed,
          ...(passed && this.label.trim() ? { label: this.label.trim() } : {}),
        });
        if (this.template.status === 'active') {
          this.step = 'done';
        } else {
          this.step = 'failed';
        }
      });
    },
    goToSample() {
      this.error = '';
      this.step = 'sample';
    },
    restart() {
      this.template = null;
      this.testQrImage = '';
      this.error = '';
      this.step = 'upload';
    },
    finish() {
      this.$emit('onActivated', this.template);
    },
  },
  watch: {
    // Render the 1 baht QR whenever we land on the test step.
    step(value) {
      if (value === 'test' && this.template && this.template.test_payload && !this.testQrImage) {
        this.showTest();
      }
    },
  },
};
</script>

<style scoped>
.qr-template-dialog .qr-text { font-size: 1.1rem; line-height: 1.75; color: rgba(0, 0, 0, 0.87); }
.qr-template-dialog .qr-hint { font-size: 1rem; line-height: 1.7; color: rgba(0, 0, 0, 0.6); }
.qr-template-dialog .qr-alert { font-size: 1.05rem; line-height: 1.7; }
.qr-template-dialog .qr-amount { color: #c62828; font-size: 1.25rem; }
.qr-template-dialog .qr-upload-box {
  text-align: center;
  padding: 28px 16px;
  border: 2px dashed #90caf9;
  border-radius: 12px;
  background: #f5faff;
}
.qr-template-dialog .qr-upload-box__text { font-size: 1.05rem; color: rgba(0, 0, 0, 0.6); }
.qr-template-dialog .qr-upload-box--over {
  border-style: solid;
  border-color: #1e88e5;
  background: #e3f2fd;
}
.qr-template-dialog .qr-image-card {
  display: inline-block;
  padding: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.qr-template-dialog .qr-refs { border: 1px solid #e0e0e0; border-radius: 8px; }
.qr-template-dialog .qr-refs td { font-size: 1.05rem; height: 46px; word-break: break-all; }
.qr-template-dialog .qr-refs .qr-ref-head { background: #eef4fb; }
.qr-template-dialog >>> .v-stepper__label { font-size: 1rem; }
.qr-template-dialog >>> .v-text-field .v-label { font-size: 1.05rem; }
.qr-template-dialog >>> .v-messages { font-size: 0.95rem; }
.qr-template-dialog >>> .v-text-field .v-messages { padding-top: 8px; padding-bottom: 8px; }
.qr-template-dialog .qr-details { border: 1px solid #e0e0e0; border-radius: 8px; }
.qr-template-dialog .qr-details__header { font-size: 1.05rem; font-weight: 600; }
.qr-template-dialog .qr-details >>> .v-expansion-panel-header__icon .v-icon { font-size: 48px; }
</style>
